import React, { useState, useEffect, useRef } from "react";
import { Router, useLocation } from "wouter";
import { LoginPage, UserData } from "./components/LoginPage";
import { HomePage } from "./components/HomePage";
import { FirstRunSetup } from "./components/FirstRunSetup";
import { AppErrorBoundary } from "./components/AppErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { isTokenExpired, getAuthToken, setAuthTokens, clearAuthTokens, getTokenExpirationTime, authAPI, setupAPI, beginSessionEnd, isSessionEnding } from "@/lib/api";
import { appDataStore } from "@/lib/appDataStore";
import { notificationsLiveStore } from "@/lib/notificationsLiveStore";
import { dataRefresh } from "@/lib/dataRefresh";
import { createSessionBroadcast, postSessionBroadcast, type SessionBroadcastEvent } from "@/lib/sessionBroadcast";
import { mergeSessionUser, persistSessionUser, sessionEventTargetsUser } from "@/lib/sessionUser";
import {
  bindActivityPing,
  flushActivityPing,
  idleExceeded,
  markSessionStarted,
  noteActivity,
  readLastActivity,
  resetIdleTimers,
  sessionLimitMs,
  shouldRefreshAccess,
} from "@/lib/idleSession";

const CURRENT_USER_FOCUS_REFRESH_TTL_MS = 60_000;

function App() {
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null);

  useEffect(() => {
    setupAPI.getStatus()
      .then((s) => setNeedsSetup(s.needsBootstrap))
      .catch(() => setNeedsSetup(false));
  }, []);

  const [currentUser, setCurrentUser] = useState<UserData | null>(() => {
    const savedUser = localStorage.getItem('currentUser');
    const token = getAuthToken();

    if (!savedUser) return null;

    // Check if token is expired
    if (isTokenExpired(token)) {
      // Strict Mode: If token is expired, logout immediately.
      // Do NOT try to use refresh token.
      clearAuthTokens();
      localStorage.removeItem('currentUser');
      return null;
    }

    return JSON.parse(savedUser);
  });
  const [, setLocation] = useLocation();
  const lastCurrentUserFocusRefreshRef = useRef(Date.now());
  const currentUserRef = useRef<UserData | null>(currentUser);

  useEffect(() => {
    currentUserRef.current = currentUser;
  }, [currentUser]);

  // Initialize dark mode from localStorage
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved ? JSON.parse(saved) : false;
  });

  // Lift state to App level to persist across user sessions
  const [presupuestos, setPresupuestos] = useState<any[]>([]);
  const [facturas, setFacturas] = useState<any[]>([]);
  const [movimientos, setMovimientos] = useState<any[]>([]);
  const [cierres, setCierres] = useState<any[]>([]);

  // Details: moved console.log to distinct effect to avoid clutter
  useEffect(() => {
    // console.log("App.tsx: movimientos state updated to:", movimientos);
  }, [movimientos]);

  useEffect(() => {
    appDataStore.setPresupuestos(presupuestos);
  }, [presupuestos]);

  useEffect(() => {
    appDataStore.setFacturas(facturas);
  }, [facturas]);

  useEffect(() => {
    appDataStore.setMovimientos(movimientos);
  }, [movimientos]);

  useEffect(() => {
    appDataStore.setCierres(cierres);
  }, [cierres]);

  // Load core cached data once and subscribe UI to store updates
  useEffect(() => {
    if (!currentUser) return;

    const sync = () => {
      const snapshot = appDataStore.getSnapshot();
      setPresupuestos(snapshot.presupuestos);
      setFacturas(snapshot.facturas);
      setMovimientos(snapshot.movimientos);
      setCierres(snapshot.cierres);
    };

    const unsubscribe = appDataStore.subscribe(sync);
    sync();

    appDataStore.ensureLoaded(30000, currentUser).catch((e) => {
      console.error("Error loading initial data:", e);
    });

    return unsubscribe;
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser?.id) {
      notificationsLiveStore.stopPolling();
      return;
    }

    const userId = currentUser.id;
    const refreshNotifications = () => {
      void notificationsLiveStore.refresh(userId, 0);
    };

    notificationsLiveStore.startPolling(
      userId,
      () => appDataStore.getSnapshot().facturas,
      () => currentUserRef.current ?? currentUser,
    );
    dataRefresh.on("notifications", refreshNotifications);
    dataRefresh.on("stock", refreshNotifications);
    window.addEventListener("notificationsUpdated", refreshNotifications);

    return () => {
      dataRefresh.off("notifications", refreshNotifications);
      dataRefresh.off("stock", refreshNotifications);
      window.removeEventListener("notificationsUpdated", refreshNotifications);
      notificationsLiveStore.stopPolling();
    };
  }, [currentUser?.id]);

  useEffect(() => {
    if (!currentUser?.codigo) return;

    const isForCurrentUser = (event: SessionBroadcastEvent) =>
      sessionEventTargetsUser(currentUserRef.current, event);

    const refreshCurrentUser = async (force = false, broadcastOnChange = false) => {
      if (!force && document.visibilityState === "hidden") return;
      const now = Date.now();
      if (!force && now - lastCurrentUserFocusRefreshRef.current < CURRENT_USER_FOCUS_REFRESH_TTL_MS) return;
      lastCurrentUserFocusRefreshRef.current = now;

      try {
        const currentUserSnapshot = currentUserRef.current;
        if (!currentUserSnapshot?.codigo) return;
        // Perfil vivo (punto_venta_default + PVs). No re-login; JWT intacto.
        const freshUser = await authAPI.getCurrentUser(currentUserSnapshot.codigo);
        if (!freshUser) return;
        const merged = mergeSessionUser(currentUserSnapshot, freshUser);
        if (!merged) return;
        const previousUserJson = JSON.stringify(currentUserRef.current);
        const freshUserJson = JSON.stringify(merged);
        if (freshUserJson !== previousUserJson) {
          setCurrentUser(merged);
          persistSessionUser(merged);
          currentUserRef.current = merged;
          if (broadcastOnChange) {
            postSessionBroadcast({
              type: "user:updated",
              userId: merged.id,
              codigo: merged.codigo,
              username: merged.username,
            });
          }
        }
      } catch (error) {
        console.error("Error refreshing current user:", error);
      }
    };

    // F5 / app load: localStorage is a snapshot — replace with /me/.
    void refreshCurrentUser(true, false);

    const broadcast = createSessionBroadcast((event) => {
      if (event.type === "logout") {
        if (!isForCurrentUser(event) && (event.codigo || event.userId || event.username)) return;
        beginSessionEnd();
        clearAuthTokens();
        localStorage.removeItem('currentUser');
        setCurrentUser(null);
        return;
      }
      if (!isForCurrentUser(event)) return;
      if (event.type === "avatar:updated") {
        dataRefresh.emit("userAvatar");
      }
      if (event.type === "user:updated" || event.type === "avatar:updated") {
        void refreshCurrentUser(true, false);
      }
    });

    const refreshCurrentUserOnFocus = () => void refreshCurrentUser(false, true);

    window.addEventListener('focus', refreshCurrentUserOnFocus);
    document.addEventListener('visibilitychange', refreshCurrentUserOnFocus);

    return () => {
      window.removeEventListener('focus', refreshCurrentUserOnFocus);
      document.removeEventListener('visibilitychange', refreshCurrentUserOnFocus);
      broadcast?.close();
    };
  }, [currentUser?.codigo]);

  const handleLogoutRef = useRef<() => void>(() => {});

  // Apply dark mode class on mount and when it changes
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(isDarkMode));
  }, [isDarkMode]);

  const logoutInFlightRef = useRef(false);

  const handleLogin = (user: UserData) => {
    logoutInFlightRef.current = false;
    markSessionStarted();
    setCurrentUser(user);
    postSessionBroadcast({
      type: "user:updated",
      userId: user.id,
      codigo: user.codigo,
      username: user.username,
    });
    setLocation('/');
  };

  const handleLogout = () => {
    if (logoutInFlightRef.current) return;
    logoutInFlightRef.current = true;
    const userSnapshot = currentUser;
    // POST con JWT todavía en localStorage; el broadcast va después (si no, el mismo tab borra el token y el logout sale 401).
    void authAPI.logout().finally(() => {
      localStorage.removeItem("currentUser");
      setCurrentUser(null);
      if (userSnapshot) {
        postSessionBroadcast({
          type: "logout",
          userId: userSnapshot.id,
          codigo: userSnapshot.codigo,
          username: userSnapshot.username,
        });
      }
    });
  };

  handleLogoutRef.current = handleLogout;

  useEffect(() => {
    if (!currentUser) {
      resetIdleTimers();
      return;
    }

    bindActivityPing(async () => {
      await authAPI.touchActivity();
    });

    const limitMs = () =>
      sessionLimitMs(
        currentUserRef.current?.sessionDuration ?? currentUserRef.current?.session_duration,
      );

    const onActivity = () => noteActivity();
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"] as const;
    for (const name of events) {
      window.addEventListener(name, onActivity, { passive: true });
    }

    const check = async () => {
      if (logoutInFlightRef.current || isSessionEnding()) return;
      const limit = limitMs();
      const now = Date.now();
      const last = readLastActivity(now);
      if (idleExceeded(last, now, limit)) {
        handleLogoutRef.current();
        return;
      }
      const exp = getTokenExpirationTime(getAuthToken());
      if (!exp) return;
      if (!shouldRefreshAccess(exp - now, now - last, limit)) return;
      try {
        await flushActivityPing();
        const result = await authAPI.refreshToken();
        if (result?.token) setAuthTokens(result.token, result.refreshToken);
      } catch (error) {
        console.error("Session: no se pudo renovar el token", error);
      }
    };

    const intervalId = window.setInterval(() => {
      void check();
    }, 5_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void check();
    };
    document.addEventListener("visibilitychange", onVisible);
    void check();

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
      for (const name of events) window.removeEventListener(name, onActivity);
      resetIdleTimers();
    };
  }, [currentUser?.id]);

  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };

  if (needsSetup === true) {
    return (
      <>
        <FirstRunSetup onComplete={() => {
          setNeedsSetup(false);
          window.location.reload();
        }} />
        <Toaster position="bottom-center" duration={3000} />
      </>
    );
  }

  return (
    <AppErrorBoundary>
      <Router base="/">
        {currentUser ? (
          <HomePage
            key={currentUser.username} // Force remount on user switch to reset state
            user={currentUser}
            onLogout={handleLogout}
            isDarkMode={isDarkMode}
            toggleDarkMode={toggleDarkMode}
            onUserUpdated={(fresh) => {
              const merged = mergeSessionUser(currentUserRef.current, fresh) ?? fresh;
              setCurrentUser(merged);
              persistSessionUser(merged);
              currentUserRef.current = merged;
            }}
            presupuestos={presupuestos}
            setPresupuestos={setPresupuestos}
            facturas={facturas}
            setFacturas={setFacturas}
            movimientos={movimientos}
            setMovimientos={setMovimientos}
            cierres={cierres}
            setCierres={setCierres}
          />
        ) : (
          <LoginPage onLogin={handleLogin} />
        )}
        <Toaster position="bottom-center" duration={3000} />
      </Router>
    </AppErrorBoundary>
  );
}

export default App;